// src/pages/user/Users.tsx
//
// Users
// - Uses standard table UI (StandardTableToolbar / StandardTableFrame / StandardPagination)
// - Keeps URL query params in sync with the applied search state (shareable deep links)
// - Uses "draft vs applied" search state to avoid filtering while typing
// - Auto-search is page-level policy and delegated to SearchPanel (no debounce here)

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, TableCell, TableRow, Tooltip } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ADMIN_USER_GROUPS, UserGroup, UserStatus, type User } from '@models/user';
import type { GetUsersParams } from '@apis/users';

import { useUserList } from '@queries/users/useUserList';
import { useUserCount } from '@queries/users/useUserCount';

import { timestampToDate } from '@utils/time';
import { filterSearchParams } from '@utils/search';

import { USER_GROUP_META, USER_STATUS_META } from '@constants/userMeta';
import { usePageHeader } from '@components/common/pageHeader.context';

import UsersSearchPanel, { type UsersSearchForm } from '@components/user/UsersSearchPanel';
import type { SetSearchValueFn } from '@components/common/search';

import {
  StandardPagination,
  StandardTableFrame,
  StandardTableToolbar,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;

// Page-level auto-search policy (SearchPanel owns the actual debounce behavior)
const PAGE_AUTO_SEARCH = { enabled: false, delayMs: 500 } as const;

type Option<T> = { label: string; value: T; description?: string };

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const parsePositiveInt = (value: string | null, fallback = 1) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

// Validates enum-like query param strings safely.
const isValidEnumValue = <T extends string>(values: readonly string[], v: string | null): v is T => {
  if (!v) return false;
  return values.includes(v);
};

/* ------------------------------------------------------------------ */
/* URL <-> state helpers                                               */
/* ------------------------------------------------------------------ */

const readSearchFromUrl = (sp: URLSearchParams): UsersSearchForm => {
  const groupRaw = sp.get('group');
  const statusRaw = sp.get('status');

  const group = isValidEnumValue<UserGroup>(Object.values(UserGroup) as unknown as string[], groupRaw)
    ? (groupRaw as UserGroup)
    : undefined;

  const status = isValidEnumValue<UserStatus>(Object.values(UserStatus) as unknown as string[], statusRaw)
    ? (statusRaw as UserStatus)
    : undefined;

  return {
    username: sp.get('username') || '',
    name: sp.get('name') || '',
    group,
    status,
  };
};

// Normalizes a search form for API/URL.
// - Trims strings
// - Drops empty values
const cleanSearchForm = (form: UsersSearchForm): GetUsersParams => {
  const username = (form.username ?? '').trim();
  const name = (form.name ?? '').trim();

  return {
    username: username ? username : undefined,
    name: name ? name : undefined,
    group: form.group || undefined,
    status: form.status || undefined,
  };
};

const buildUrlParams = (form: UsersSearchForm, page: number) => {
  const cleaned = cleanSearchForm(form);

  const stringParams = Object.entries(cleaned).reduce<Record<string, string>>((acc, [k, v]) => {
    if (v === undefined || v === null) return acc;
    acc[k] = String(v);
    return acc;
  }, {});

  return {
    ...filterSearchParams(stringParams),
    page: String(page),
  };
};

const Users = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /* ------------------------------------------------------------------ */
  /* Header actions                                                      */
  /* ------------------------------------------------------------------ */

  const { setPageHeaderSlots } = usePageHeader();

  useEffect(() => {
    setPageHeaderSlots({
      actions: [
        {
          key: 'add-user',
          label: 'Add User',
          startIcon: <Add />,
          variant: 'contained',
          primary: true,
          onClick: () => navigate('/users/new'),
        },
      ],
    });

    // Clear on unmount to avoid action leakage across routes.
    return () => setPageHeaderSlots({});
  }, [navigate, setPageHeaderSlots]);

  /* ------------------------------------------------------------------ */
  /* Options (labels/descriptions from centralized meta)                 */
  /* ------------------------------------------------------------------ */

  const groupOptions = useMemo<Option<UserGroup>[]>(() => {
    return ADMIN_USER_GROUPS.map((g) => ({
      value: g,
      label: USER_GROUP_META[g]?.label ?? String(g),
      description: USER_GROUP_META[g]?.description ?? '',
    }));
  }, []);

  const statusOptions = useMemo<Option<UserStatus>[]>(() => {
    const values = Object.values(UserStatus) as UserStatus[];
    return values.map((s) => ({
      value: s,
      label: USER_STATUS_META[s]?.label ?? String(s),
      description: USER_STATUS_META[s]?.description ?? '',
    }));
  }, []);

  /* ------------------------------------------------------------------ */
  /* Search state (draft vs applied) + URL sync                          */
  /* ------------------------------------------------------------------ */

  const [appliedSearch, setAppliedSearch] = useState<UsersSearchForm>(() => readSearchFromUrl(searchParams));
  const [draftSearch, setDraftSearch] = useState<UsersSearchForm>(() => ({ ...readSearchFromUrl(searchParams) }));
  const [page, setPage] = useState<number>(() => parsePositiveInt(searchParams.get('page'), 1));

  const [filtersOpen, setFiltersOpen] = useState(false);

  // Sync local state when URL changes (deep-linking, back/forward).
  useEffect(() => {
    const nextApplied = readSearchFromUrl(searchParams);
    const nextPage = parsePositiveInt(searchParams.get('page'), 1);

    setAppliedSearch(nextApplied);

    setDraftSearch((prev) => {
      const a = cleanSearchForm(prev);
      const b = cleanSearchForm(nextApplied);

      const same =
        (a.username ?? '') === (b.username ?? '') &&
        (a.name ?? '') === (b.name ?? '') &&
        (a.group ?? null) === (b.group ?? null) &&
        (a.status ?? null) === (b.status ?? null);

      return same ? prev : nextApplied;
    });

    setPage(nextPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const cleanedApplied = useMemo(() => cleanSearchForm(appliedSearch), [appliedSearch]);

  const syncUrlFromApplied = useCallback(
    (nextApplied: UsersSearchForm, nextPage: number) => {
      setSearchParams(buildUrlParams(nextApplied, nextPage));
    },
    [setSearchParams],
  );

  const applyDraftAsApplied = useCallback(
    (nextDraft?: UsersSearchForm) => {
      const next = nextDraft ?? draftSearch;

      setAppliedSearch(next);
      setPage(1);
      syncUrlFromApplied(next, 1);
    },
    [draftSearch, syncUrlFromApplied],
  );

  /* ------------------------------------------------------------------ */
  /* Draft setter used by shared inputs                                  */
  /* ------------------------------------------------------------------ */

  const setValue: SetSearchValueFn<UsersSearchForm> = useCallback((key, value) => {
    setDraftSearch((prev) => {
      const next = { ...prev } as Record<string, unknown>;

      // Text fields keep "" in draft; selects keep undefined when cleared.
      if (key === 'username' || key === 'name') {
        next[key] = (value ?? '') as string;
      } else {
        next[key] = (value ?? undefined) as unknown;
      }

      return next as UsersSearchForm;
    });
  }, []);

  /* ------------------------------------------------------------------ */
  /* Search / Reset                                                      */
  /* ------------------------------------------------------------------ */

  const handleSearch = useCallback(() => {
    applyDraftAsApplied();
  }, [applyDraftAsApplied]);

  const handleReset = useCallback(() => {
    const cleared: UsersSearchForm = {
      username: '',
      name: '',
      group: undefined,
      status: undefined,
    };

    setDraftSearch(cleared);
    applyDraftAsApplied(cleared);
  }, [applyDraftAsApplied]);

  /* ------------------------------------------------------------------ */
  /* Queries                                                             */
  /* ------------------------------------------------------------------ */

  const { data: users, isLoading: isLoadingList } = useUserList({
    page,
    pageSize: PAGE_SIZE,
    searchParam: cleanedApplied,
  });

  const { data: totalCount, isLoading: isLoadingCount } = useUserCount(cleanedApplied);

  const safeTotalCount = totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(safeTotalCount / PAGE_SIZE));
  const rows: User[] = users?.data ?? [];

  const isTableLoading = Boolean(isLoadingList || isLoadingCount);

  /* ------------------------------------------------------------------ */
  /* Paging                                                              */
  /* ------------------------------------------------------------------ */

  const handleChangePage = (next: number) => {
    const safeNext = clamp(next, 1, totalPages);
    setPage(safeNext);
    syncUrlFromApplied(appliedSearch, safeNext);
  };

  /* ------------------------------------------------------------------ */
  /* Derived selections                                                   */
  /* ------------------------------------------------------------------ */

  const selectedGroup = useMemo(
    () => groupOptions.find((o) => o.value === draftSearch.group) ?? null,
    [draftSearch.group, groupOptions],
  );

  const selectedStatus = useMemo(
    () => statusOptions.find((o) => o.value === draftSearch.status) ?? null,
    [draftSearch.status, statusOptions],
  );

  // (# + ID + Name + Group + Position + E-mail + Phone + Status + Recent login)
  const columnsCount = 9;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <UsersSearchPanel
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        draftSearch={draftSearch}
        setValue={setValue}
        groupOptions={groupOptions}
        statusOptions={statusOptions}
        selectedGroup={selectedGroup}
        selectedStatus={selectedStatus}
        onSearch={handleSearch}
        onReset={handleReset}
        autoSearch={PAGE_AUTO_SEARCH}
      />

      <StandardTableToolbar
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={safeTotalCount}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      <Box mt={1}>
        <StandardTableFrame<User>
          containerSx={{ borderRadius: 2 }}
          minWidth={1100}
          columnsCount={columnsCount}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No users found."
          getRowStatus={(row) => (row.status === UserStatus.INACTIVE ? 'inactive' : 'normal')}
          head={
            <TableRow>
              <TableCell align="right" width={70}>
                #
              </TableCell>
              <TableCell align="center">ID</TableCell>
              <TableCell align="center">Name</TableCell>
              <TableCell align="center">Group</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Recent login</TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(row) => String(row.userId)}
          renderRow={(row, index) => {
            const groupMeta = USER_GROUP_META[row.group];
            const statusMeta = USER_STATUS_META[row.status];

            const onGoDetail = () => navigate(`/users/${row.userId}`);

            const groupHint = groupMeta?.description?.trim() ?? '';
            const statusHint = statusMeta?.description?.trim() ?? '';

            return (
              <>
                <TableCell align="right" sx={cellSx.number}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={onGoDetail}
                    sx={{
                      minWidth: 0,
                      px: 0.5,
                      fontWeight: 800,
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    {(page - 1) * PAGE_SIZE + index + 1}
                  </Button>
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {row.username}
                </TableCell>

                <TableCell align="center">{row.name}</TableCell>

                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  {groupHint ? (
                    <Tooltip title={groupHint} arrow>
                      <Chip
                        size="small"
                        label={groupMeta?.label ?? row.group}
                        color={groupMeta?.chip.color ?? 'default'}
                        variant={groupMeta?.chip.variant ?? 'outlined'}
                        sx={{ fontWeight: 800 }}
                      />
                    </Tooltip>
                  ) : (
                    <Chip
                      size="small"
                      label={groupMeta?.label ?? row.group}
                      color={groupMeta?.chip.color ?? 'default'}
                      variant={groupMeta?.chip.variant ?? 'outlined'}
                      sx={{ fontWeight: 800 }}
                    />
                  )}
                </TableCell>

                <TableCell>{row.position}</TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>{row.phoneNumber}</TableCell>

                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  {statusHint ? (
                    <Tooltip title={statusHint} arrow>
                      <Chip
                        size="small"
                        label={statusMeta?.label ?? row.status}
                        color={statusMeta?.chip.color ?? 'default'}
                        variant={statusMeta?.chip.variant ?? 'outlined'}
                        sx={{ fontWeight: 800 }}
                      />
                    </Tooltip>
                  ) : (
                    <Chip
                      size="small"
                      label={statusMeta?.label ?? row.status}
                      color={statusMeta?.chip.color ?? 'default'}
                      variant={statusMeta?.chip.variant ?? 'outlined'}
                      sx={{ fontWeight: 800 }}
                    />
                  )}
                </TableCell>

                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  {row.recentLoginTime ? timestampToDate(row.recentLoginTime) : '-'}
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={safeTotalCount}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default Users;
